import { Amplify } from 'aws-amplify';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { updatePassword, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { updatePatient } from '@/graphql/mutations';

import Alert, { AlertColor } from '@mui/material/Alert';
import awsconfig from '@/amplifyconfiguration.json';

import { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { PatientInformationType } from '../types/PatientInformationType';
import { PasswordType } from '../types/PasswordType';

import { Backdrop, Box, CircularProgress, Grid, Stack, TextField, Typography, Button, Link } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getPatient } from '@/graphql/queries';
import { getWalletState } from '../../mynumber/constants/Api';
import { web3APIClient } from '@/client/axios';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { GetPatientQuery, UpdatePatientMutation } from '@/API';
import { executeQuery } from '@/client/graphql';

Amplify.configure(awsconfig);

const patientInfoSchema: yup.ObjectSchema<PatientInformationType> = yup.object({
  familyName: yup.string().required(),
  firstName: yup.string().required(),
  familyNameKana: yup.string().required(),
  firstNameKana: yup.string().required(),
  email: yup
    .string()
    .required('メールアドレスを入力してください。')
    .email('メールアドレスを正しい形式で入力してください。'),
  phoneNumber: yup
    .string()
    .matches(/^[0-9]{10,11}$/, '電話番号は半角数字11文字以内で入力してください。')
    .required('電話番号を入力してください。'),
  password: yup.string().required(),
  dateOfBirth: yup.string().required()
});

const passwordSchema: yup.ObjectSchema<PasswordType> = yup.object({
  oldPassword: yup.string().required('現在のパスワードを入力してください。'),
  newPassword: yup
    .string()
    .required('新しいパスワードを入力してください。')
    .min(8, 'パスワードは8文字以上を入力してください。')
    .max(100, 'パスワードが長すぎます。')
    .matches(/.*[A-Z].*/, '大文字を1文字以上含めてください。')
    .matches(/.*[a-z].*/, '小文字を1文字以上含めてください。')
    .matches(/.*[0-9].*/, '数字を1文字以上含めてください。')
    .matches(
      /.*[\^\$\*\.\[\]\{\}\(\)\?\-"\!@\#%&\/\\,\>\<'\:;\|_~`\+\=].*/,
      '記号(^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + =)を1文字以上含めてください。'
    ),
  newPasswordConfirm: yup
    .string()
    .required('新しいパスワードを入力してください。')
    .min(8, 'パスワードは8文字以上を入力してください。')
    .max(100, 'パスワードが長すぎます。')
    .matches(/.*[A-Z].*/, '大文字を1文字以上含めてください。')
    .matches(/.*[a-z].*/, '小文字を1文字以上含めてください。')
    .matches(/.*[0-9].*/, '数字を1文字以上含めてください。')
    .matches(
      /.*[\^\$\*\.\[\]\{\}\(\)\?\-"\!@\#%&\/\\,\>\<'\:;\|_~`\+\=].*/,
      '記号(^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + =)を1文字以上含めてください。'
    )
    .oneOf([yup.ref('newPassword')], '同じパスワードを入力してください。')
});

export function PatientInformationChangeForm(props: any) {
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [isPasswordForm, setIsPasswordForm] = useState<boolean>(false);
  const [updateMessage, setUpdateMessage] = useState<{ type: string; message: string } | null>(null);
  const [passwordSubmitDisabled, setPasswordSubmitDisabled] = useState(false);
  const { handleChangeEmail } = useCognitoAuth();

  const patientNameValue = () => {
    const formValue = getValues();
    const famName = formValue.familyName;
    const firName = formValue.firstName;
    const famNameKana = formValue.familyNameKana;
    const firNameKana = formValue.firstNameKana;
    return `${famName}　${firName}（${famNameKana}　${firNameKana}）`;
  };

  const dateOfBirthValue = () => {
    const dateOfBirth: string[] = getValues().dateOfBirth.split('-');
    const birthYear = dateOfBirth[0];
    const birthMonth = dateOfBirth[1].replace(/(^0)/, '');
    const birthDate = dateOfBirth[2].replace(/(^0)/, '');
    return `${birthYear}年 ${birthMonth}月 ${birthDate}日`;
  };

  const passwordValue = (numberOfRepeat?: number) => {
    return '●'.repeat(numberOfRepeat || 12);
  };

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm<PatientInformationType>({
    defaultValues: {
      ...(props.currentState.patientInformation || {
        familyName: '',
        firstName: '',
        familyNameKana: '',
        firstNameKana: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: ''
      }),
      password: passwordValue()
    },
    resolver: yupResolver(patientInfoSchema)
  });

  const passes = useForm<PasswordType>({
    defaultValues: { oldPassword: '', newPassword: '', newPasswordConfirm: '' },
    resolver: yupResolver(passwordSchema)
  });

  async function changePassword(data: PasswordType) {
    setBackdropOpen(true);
    try {
      await updatePassword({ oldPassword: data.oldPassword, newPassword: data.newPassword });
      setUpdateMessage({ type: 'success', message: 'パスワードを変更しました。' });
      setPasswordSubmitDisabled(true);
    } catch (e: any) {
      console.error(e as string);
      switch (e.name) {
        case 'LimitExceededException':
          setUpdateMessage({
            type: 'error',
            message: `制限に達したため、パスワードを変更できません。数分後にもう一度お試しください。`
          });
          break;
        case 'NotAuthorizedException':
          setUpdateMessage({
            type: 'error',
            message: `パスワードが変更できませんでした。現在のパスワードが間違っています。`
          });
          break;
        case 'UserUnAuthenticatedException':
          setUpdateMessage({ type: 'error', message: `恐れ入りますが、一度ログインし直してからお試しください。` });
          break;
        default:
          setUpdateMessage({
            type: 'error',
            message: `パスワードが変更できませんでした。エラーコード：${e as string}`
          });
          break;
      }
    }
    setBackdropOpen(false);
  }

  async function updatePatientInfo(data: PatientInformationType) {
    try {
      const cognitoId = (await fetchUserAttributes()).sub!;
      const emailChanged = data.email !== props.currentState.patientInformation.email;

      if (emailChanged) {
        // **メールアドレスが変更された場合の処理**
        props.onPatientInformationSet(data.email, data.phoneNumber);

        // DyanamoDBへの保存処理はメールアドレスの検証時に行う
        handleChangeEmailAndSendCode();
        return;
      }

      const patientInfo = await executeQuery<GetPatientQuery>(
        getPatient,
        {
          id: cognitoId
        },
        'userPool'
      );

      await executeQuery<UpdatePatientMutation>(
        updatePatient,
        {
          input: { id: patientInfo.getPatient!.id!, phoneNumber: data.phoneNumber, email: data.email }
        },
        'userPool'
      );

      // web3:患者情報更新API呼出(メールアドレスの変更以外)
      if (await getWalletState()) {
        const session = await fetchAuthSession({ forceRefresh: true });
        const idToken = session.tokens?.idToken ?? '';
        const config = {
          headers: { Authorization: idToken.toString() }
        };
        await web3APIClient.put('v2/patients/info', null, config);
      }

      props.linkToMypage();
    } catch (e: any) {
      console.error(e);
      if (notMatchCustomErrorMessage(e.name)) {
        Sentry.captureException(e);
      }

      switch (e.name) {
        case 'UserUnAuthenticatedException':
          setUpdateMessage({ type: 'error', message: `恐れ入りますが、一度ログインし直してからお試しください。` });
          break;
        default:
          setUpdateMessage({ type: 'error', message: `お客様情報を更新できませんでした。\nエラーメッセージ：${e}` });
          break;
      }
    }
  }

  const ValueShower = (props: { label: string; text: string }) => {
    return (
      <Grid item>
        <Stack direction={'column'} spacing={1}>
          <div>
            <Typography fontSize={14} className="font-bold" ml={0.5} color={'inherit'}>
              {props.label}
            </Typography>
          </div>
          <Typography fontSize={16}>{props.text}</Typography>
        </Stack>
      </Grid>
    );
  };

  async function handleChangeEmailAndSendCode() {
    const formValues = getValues();
    const email = formValues.email;
    try {
      setBackdropOpen(true);
      await handleChangeEmail(email);
      props.setActiveStep(9);
    } catch (e: any) {
      setUpdateMessage({ type: 'error', message: `恐れ入りますが、一度ログインし直してからお試しください。` });
      setBackdropOpen(false);
      console.log(e);
    }
  }

  const TextInputs = (props: {
    label: string;
    placeholder: string;
    property: keyof PatientInformationType;
    row?: boolean;
    type?: string;
  }) => {
    const property = props.property;
    const rowBlocks = props.row || false ? { xs: 6, sm: 12 } : {};
    return (
      <Grid item {...rowBlocks}>
        <Stack direction={'column'} spacing={1}>
          <div>
            <Typography fontSize={14} ml={0.5} className="font-bold" color={'inherit'}>
              {props.label}
            </Typography>
          </div>

          <TextField
            type={props.type || 'text'}
            placeholder={props.placeholder}
            fullWidth
            size="small"
            sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
            error={!!errors[property]}
            {...register(property)}
          />
        </Stack>
        <ErrorMessage message={errors[property]?.message} />
      </Grid>
    );
  };

  const SubmitButton = (props: { label: string; disabled?: boolean }) => {
    return (
      <Button
        disabled={props.disabled}
        type="submit"
        fullWidth
        sx={{ height: '48px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
        className="bg-primary flex relative rounded"
      >
        <div className="text-base m-auto font-bold">{props.label}</div>
      </Button>
    );
  };

  const BackButton = (props: { onClick: () => void }) => {
    return (
      <Button
        type="button"
        variant="outlined"
        fullWidth
        color="inherit"
        sx={{ height: '48px', textAlign: 'center', margin: 'auto' }}
        onClick={props.onClick}
      >
        <div className="text-base m-auto font-bold">戻る</div>
      </Button>
    );
  };

  const BackDrop = (props: { isOpen: boolean }) => {
    return (
      <Backdrop
        sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={props.isOpen}
      >
        <CircularProgress color={'inherit'} />
      </Backdrop>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container direction={'row'} justifyContent={'center'} spacing={2}>
        <Grid item xs={12} sm={8} mt={2}>
          {!!!isPasswordForm ? (
            <form onSubmit={handleSubmit(updatePatientInfo)}>
              <Grid container direction={'column'} spacing={2}>
                <ValueShower text={patientNameValue()} label="氏名" />
                <ValueShower text={dateOfBirthValue()} label="生年月日" />
                <TextInputs
                  label={'メールアドレス'}
                  placeholder={'example@healthinterview.clinic'}
                  property={'email'}
                />
                <TextInputs label={'電話番号'} placeholder={'09012345678'} property={'phoneNumber'} />
                <ValueShower text={passwordValue()} label="パスワード" />
                <Link
                  ml={'auto'}
                  fontSize={14}
                  onClick={() => {
                    setIsPasswordForm(true);
                    setPasswordSubmitDisabled(false);
                    setUpdateMessage(null);
                  }}
                >
                  パスワードを変更したい場合はこちら
                </Link>
                <Grid item sx={{ mt: 2 }}>
                  <div className="text-primary">
                    <SubmitButton label="変更を保存する" />
                    <div style={{ margin: '1rem 0' }}></div>
                    <BackButton
                      onClick={() => {
                        props.linkToMypage();
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            </form>
          ) : (
            <form onSubmit={passes.handleSubmit(changePassword)}>
              <Grid container direction={'column'} spacing={2}>
                <PasswordInputs
                  label={'現在のパスワード'}
                  placeholder={'8文字以上(大文字・小文字・数字・記号を含む'}
                  property={'oldPassword'}
                  passes={passes}
                />
                <PasswordInputs
                  label={'新しいパスワード'}
                  placeholder={'8文字以上(大文字・小文字・数字・記号を含む'}
                  property={'newPassword'}
                  passes={passes}
                  desc
                />
                <PasswordInputs
                  label={'新しいパスワード（確認）'}
                  placeholder={'8文字以上(大文字・小文字・数字・記号を含む'}
                  property={'newPasswordConfirm'}
                  passes={passes}
                  desc
                />
                <Grid item>
                  <div className="text-primary">
                    <SubmitButton label="パスワードを変更する" disabled={passwordSubmitDisabled} />
                    <div style={{ margin: '1rem 0' }}></div>
                    <BackButton
                      onClick={() => {
                        setIsPasswordForm(false);
                        setPasswordSubmitDisabled(false);
                        setUpdateMessage(null);
                        passes.reset();
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            </form>
          )}
          {updateMessage !== null && (
            <Alert severity={updateMessage.type as AlertColor} sx={{ marginTop: '1rem' }}>
              {updateMessage.message}
            </Alert>
          )}
        </Grid>
      </Grid>
      <BackDrop isOpen={backdropOpen} />
    </Box>
  );
}

const PasswordInputs = (props: {
  label: string;
  placeholder: string;
  property: keyof PasswordType;
  desc?: boolean;
  passes: UseFormReturn<PasswordType, any, undefined>;
}) => {
  const property = props.property;
  const ers = props.passes.formState.errors[property];
  return (
    <Grid item>
      <Stack direction={'column'} spacing={1}>
        <Typography fontSize={16} ml={0.5} className="font-bold" color={'inherit'}>
          {props.label}
        </Typography>

        <TextField
          type={'password'}
          placeholder={props.placeholder}
          fullWidth
          size="small"
          sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
          error={!!ers}
          {...props.passes.register(property)}
        />
      </Stack>
      <ErrorMessage
        message={
          ers?.type === 'required' || ers?.types === undefined
            ? ers?.message
            : Object.values(ers.types || {})
                .map((v) => (Array.isArray(v) ? v.join('') : v))
                .join('')
        }
      />
    </Grid>
  );
};

const ErrorMessage = (props: { message: string | undefined }) => {
  if (!props.message) return;
  return (
    <Typography color="error" style={{ fontSize: '12px', marginLeft: '4px' }}>
      {props.message}
    </Typography>
  );
};
